import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Send, MessageSquare } from 'lucide-react';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  ...(localStorage.getItem('nebb_token')
    ? { Authorization: `Bearer ${localStorage.getItem('nebb_token')}` }
    : {}),
});

const hora = (iso) => new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' });
const fecha = (iso) => new Date(iso).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' });

export default function ChatPage() {
  const { users, currentUser } = useApp();
  const [summary, setSummary] = useState([]);     // [{userId, lastText, lastAt, unread}]
  const [activeId, setActiveId] = useState(null); // interlocutor abierto
  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  const otros = users.filter(u => u.id !== currentUser?.id);
  const getUser = id => users.find(u => u.id === id) || {};
  const sumOf = id => summary.find(s => s.userId === id);

  const cargarSummary = useCallback(async () => {
    try {
      const r = await fetch('/api/messages/summary', { headers: authHeaders() });
      const d = await r.json();
      if (Array.isArray(d)) setSummary(d);
    } catch {}
  }, []);

  const cargarThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await fetch(`/api/messages/${id}`, { headers: authHeaders() });
      const d = await r.json();
      if (Array.isArray(d) && activeRef.current === id) setThread(d);
    } catch {}
  }, []);

  // Poll: resumen cada 8s, hilo activo cada 4s
  useEffect(() => {
    cargarSummary();
    const i = setInterval(cargarSummary, 8000);
    return () => clearInterval(i);
  }, [cargarSummary]);

  useEffect(() => {
    if (!activeId) return;
    cargarThread(activeId);
    const i = setInterval(() => cargarThread(activeId), 4000);
    return () => clearInterval(i);
  }, [activeId, cargarThread]);

  // Auto-scroll al final cuando llegan mensajes
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread.length, activeId]);

  const enviar = async () => {
    const t = text.trim();
    if (!t || !activeId || sending) return;
    setSending(true);
    try {
      const r = await fetch(`/api/messages/${activeId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ text: t }),
      });
      const msg = await r.json();
      if (msg.id) { setThread(th => [...th, msg]); setText(''); }
    } catch {}
    setSending(false);
  };

  const abrir = (id) => {
    setActiveId(id);
    setThread([]);
    // Al abrir, los no leídos de ese hilo se marcan en el server; reflejar local
    setSummary(s => s.map(x => x.userId === id ? { ...x, unread: 0 } : x));
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Chat</h1>
          <p className="page-subtitle">Mensajes directos entre el equipo</p>
        </div>
      </div>

      <div className={`chat-grid ${activeId ? 'chat-open' : ''}`} style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>

        {/* Lista de compañeros */}
        <div className="card chat-list" style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {otros.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-muted)' }}>No hay otros usuarios cargados.</div>
          )}
          {otros.map(u => {
            const s = sumOf(u.id);
            const activo = activeId === u.id;
            return (
              <button
                key={u.id}
                onClick={() => abrir(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none',
                  cursor: 'pointer',
                  background: activo ? 'var(--accent-dim)' : 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 17, flexShrink: 0,
                  background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13, color: 'var(--accent)',
                }}>
                  {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.email}</div>
                  {s?.lastText && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.lastText}</div>
                  )}
                </div>
                {s?.unread > 0 && (
                  <span style={{ background: 'var(--accent)', color: '#06281f', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '2px 7px', flexShrink: 0 }}>{s.unread}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Hilo de conversación */}
        <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '70vh', overflow: 'hidden' }}>
          {!activeId ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 10 }}>
              <MessageSquare size={32} style={{ opacity: 0.3 }} />
              <span style={{ fontSize: 13 }}>Elegí un compañero para empezar a chatear</span>
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button className="chat-back btn btn-secondary btn-sm" onClick={() => setActiveId(null)} style={{ padding: '4px 10px' }}>←</button>
                {getUser(activeId).name || getUser(activeId).email}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {thread.map((m, i) => {
                  const mio = m.from === currentUser?.id;
                  const prevDay = i > 0 ? fecha(thread[i - 1].at) : '';
                  const showDay = fecha(m.at) !== prevDay;
                  return (
                    <React.Fragment key={m.id}>
                      {showDay && (
                        <div style={{ textAlign: 'center', fontSize: 10.5, color: 'var(--text-muted)', margin: '6px 0' }}>{fecha(m.at)}</div>
                      )}
                      <div style={{
                        alignSelf: mio ? 'flex-end' : 'flex-start',
                        maxWidth: '75%',
                        background: mio ? 'var(--accent-dim)' : 'var(--bg-input)',
                        border: mio ? '1px solid rgba(124,108,248,0.25)' : '1px solid var(--border)',
                        borderRadius: 12,
                        padding: '8px 12px',
                      }}>
                        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{hora(m.at)}</div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0 }}>
                <textarea
                  rows={1}
                  placeholder="Escribí un mensaje..."
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                  style={{ flex: 1, resize: 'none' }}
                />
                <button className="btn btn-primary" disabled={sending || !text.trim()} onClick={enviar}>
                  <Send size={14} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
